// Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
// See LICENSE in the project root for license information.
var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v;
import globalStyles from './styles/global.module.css';
import graphStyles from './styles/graphView.module.css';
import selectionStyles from './styles/selectionBar.module.css';
import tableStyles from './styles/tableView.module.css';
import terminalStyles from './styles/terminalPane.module.css';
import topBarStyles from './styles/topBar.module.css';
import { applyExecutionStates as applyExecutionStatesMutation, patchOperationsFromPayload, setOperationsFromPayload, setQueuedStates, toLastExecutionResultsMap } from './modules/dashboardMutations';
import { computeWsUrl as computeWebSocketUrl, overallStatusText, setConnected as setTopBarConnected, showConnectingStatus, updateDerivedUrlDisplay as updateTopBarDerivedUrlDisplay, updateManagerState as updateTopBarManagerState, updateStatusPill as updateTopBarStatusPill } from './modules/topBar';
import { createTerminalPaneController } from './modules/terminalPane';
import { createDashboardWebSocketController } from './modules/dashboardWebSocket';
import { computeFilterSetsCore } from './modules/graphFiltering';
import { createGraphViewController, graphState } from './modules/graphView';
import { createGraphSelectionController } from './modules/graphSelection';
import { createSelectionBarController } from './modules/selectionBar';
import { createTableViewController } from './modules/tableView';
import { createPhaseLegendController } from './modules/phaseLegend';
import { wireLeftBarActions } from './modules/leftBar';
import { wireMainBarActions } from './modules/mainBar';
import { wireViewBar } from './modules/viewBar';
import { loadDashboardUrlState } from './modules/urlState';
import { buildRunPolicyText, buildTooltip, computeDisplayStatus as computeDisplayStatusCore, enabledGlyph, getStatusColors, statusEmoji } from './modules/statusHelpers';
function addClasses(id, ...classNames) {
    var _a;
    (_a = document.getElementById(id)) === null || _a === void 0 ? void 0 : _a.classList.add(...classNames);
}
function applyStaticStyles() {
    document.body.classList.add(globalStyles.dashboard);
    addClasses('top-bar', topBarStyles.topBar);
    addClasses('overall-status', topBarStyles.overallStatus);
    addClasses('status-emoji', topBarStyles.statusEmoji);
    addClasses('status-pill', globalStyles.statusPill, topBarStyles.statusPill, globalStyles.statusDisconnected);
    addClasses('app-title', globalStyles.appTitle);
    addClasses('actions', globalStyles.actions, topBarStyles.actions);
    addClasses('graph-state', globalStyles.graphState, topBarStyles.graphState);
    addClasses('view-controls', globalStyles.viewControls, topBarStyles.viewControls);
    addClasses('parallelism-label', globalStyles.flexRow, globalStyles.parallelismLabel);
    addClasses('parallelism-input', globalStyles.parallelismInput);
    addClasses('search-label', globalStyles.searchLabel);
    addClasses('name-search', globalStyles.nameSearch);
    addClasses('top-bar-spacer', globalStyles.flexSpacer);
    addClasses('connection-form', globalStyles.flexRow, globalStyles.connectionForm, topBarStyles.connectionForm);
    addClasses('connect-btn', topBarStyles.connectButton);
    const iconButtonIds = [
        'play-pause-btn',
        'execute-btn',
        'abort-execution-btn',
        'debug-btn',
        'verbose-btn',
        'select-visible-btn',
        'connect-btn',
        'toggle-terminal-btn',
        'term-clear-btn',
        'term-autoscroll-btn'
    ];
    iconButtonIds.forEach((id) => addClasses(id, globalStyles.iconBtn));
    addClasses('abort-execution-btn', globalStyles.stop);
    ['debug-btn', 'verbose-btn', 'connect-btn', 'toggle-terminal-btn', 'term-autoscroll-btn'].forEach((id) => addClasses(id, globalStyles.toggle));
    addClasses('selection-bar', selectionStyles.selectionBar);
    addClasses('selection-heading', selectionStyles.selectionHeading);
    addClasses('selection-actions', selectionStyles.selectionActions);
    const actionButtonIds = [
        'clear-selection-btn',
        'invalidate-btn',
        'close-runners-btn',
        'set-enabled-default-btn',
        'set-enabled-ignore-deps-btn',
        'set-enabled-disabled-btn',
        'selection-mode-btn',
        'expand-deps-btn',
        'expand-consumers-btn'
    ];
    actionButtonIds.forEach((id) => addClasses(id, globalStyles.action));
    addClasses('clear-selection-btn', selectionStyles.selectionCountButton);
    addClasses('selection-mode-btn', globalStyles.toggle);
    addClasses('content-wrap', globalStyles.contentWrap);
    addClasses('main', globalStyles.main);
    addClasses('left', globalStyles.pane, globalStyles.leftPane);
    addClasses('right', globalStyles.pane);
    addClasses('operations-table-container', tableStyles.operationsTableContainer);
    addClasses('operations-table', tableStyles.operationsTable);
    addClasses('table-stats', tableStyles.tableStats);
    addClasses('graph-container', graphStyles.graphContainer);
    addClasses('phase-pane', graphStyles.phasePane);
    addClasses('phase-groups', graphStyles.phaseGroups);
    addClasses('graph-wrapper', graphStyles.graphWrapper);
    addClasses('graph', graphStyles.graph);
    addClasses('graph-legend', graphStyles.graphLegend);
    addClasses('resizer', terminalStyles.resizer);
    addClasses('terminal', terminalStyles.terminalContainer);
    addClasses('terminal-header', terminalStyles.terminalHeader);
    addClasses('terminal-title', terminalStyles.terminalTitle);
    addClasses('terminal-controls', terminalStyles.terminalControls);
    addClasses('terminal-body', terminalStyles.terminalBody);
    addClasses('term-autoscroll', terminalStyles.termAutoscroll);
    addClasses('toggle-terminal-btn', terminalStyles.toggleTerminalButton);
    addClasses('term-autoscroll-icon', terminalStyles.vertical);
}
applyStaticStyles();
const statusPill = document.getElementById('status-pill');
const statusEmojiEl = document.getElementById('status-emoji');
const connectBtn = (_a = document.getElementById('connect-btn')) !== null && _a !== void 0 ? _a : undefined;
const appTitleEl = (_b = document.getElementById('app-title')) !== null && _b !== void 0 ? _b : undefined;
const tableEl = document.getElementById('operations-table');
const tableHead = (_c = tableEl.querySelector('thead')) !== null && _c !== void 0 ? _c : undefined;
const tableBody = (_d = tableEl.querySelector('tbody')) !== null && _d !== void 0 ? _d : undefined;
const tableStats = (_e = document.getElementById('table-stats')) !== null && _e !== void 0 ? _e : undefined;
const managerStateEl = (_f = document.getElementById('graph-state')) !== null && _f !== void 0 ? _f : undefined;
const edgesSvg = (_g = document.querySelector('svg#edges')) !== null && _g !== void 0 ? _g : undefined;
if (!edgesSvg) {
    throw new Error('The graph edges SVG element was not found.');
}
const graphEl = document.getElementById('graph');
const legendEl = (_h = document.getElementById('graph-legend')) !== null && _h !== void 0 ? _h : undefined;
const phaseGroupsEl = (_j = document.getElementById('phase-groups')) !== null && _j !== void 0 ? _j : undefined;
const playPauseBtn = (_k = document.getElementById('play-pause-btn')) !== null && _k !== void 0 ? _k : undefined;
const parallelismInput = (_l = document.getElementById('parallelism-input')) !== null && _l !== void 0 ? _l : undefined;
const debugBtn = (_m = document.getElementById('debug-btn')) !== null && _m !== void 0 ? _m : undefined;
const verboseBtn = (_o = document.getElementById('verbose-btn')) !== null && _o !== void 0 ? _o : undefined;
const terminalEl = (_p = document.getElementById('terminal')) !== null && _p !== void 0 ? _p : undefined;
const terminalBody = (_q = document.getElementById('terminal-body')) !== null && _q !== void 0 ? _q : undefined;
const termClearBtn = (_r = document.getElementById('term-clear-btn')) !== null && _r !== void 0 ? _r : undefined;
const termAutoScroll = (_s = document.getElementById('term-autoscroll')) !== null && _s !== void 0 ? _s : undefined;
const termAutoscrollBtn = (_t = document.getElementById('term-autoscroll-btn')) !== null && _t !== void 0 ? _t : undefined;
const toggleTerminalBtn = (_u = document.getElementById('toggle-terminal-btn')) !== null && _u !== void 0 ? _u : undefined;
const resizerEl = (_v = document.getElementById('resizer')) !== null && _v !== void 0 ? _v : undefined;
const terminalPane = createTerminalPaneController({
    terminalEl,
    terminalBody,
    termClearBtn,
    termAutoScrollCheckbox: termAutoScroll,
    termAutoscrollBtn,
    toggleTerminalBtn,
    resizerEl
});
const topBarRefs = {
    connectBtn,
    statusPill,
    statusEmojiEl,
    debugBtn,
    verboseBtn,
    playPauseBtn,
    parallelismInput,
    managerStateEl
};
const disabledControlIds = [
    'invalidate-btn',
    'close-runners-btn',
    'set-enabled-default-btn',
    'set-enabled-ignore-deps-btn',
    'set-enabled-disabled-btn',
    'expand-deps-btn',
    'expand-consumers-btn',
    'execute-btn',
    'abort-execution-btn',
    'clear-selection-btn',
    'debug-btn',
    'verbose-btn',
    'parallelism-input',
    'play-pause-btn'
];
const operations = new Map();
const executionStates = new Map();
const queuedStates = new Map();
let lastExecutionResults = new Map();
let selection = new Set();
let graphSettings;
let currentView = 'table';
let currentFilter = 'all';
let searchQuery = '';
let filteredOutNames = new Set();
let searchFilteredOutNames = new Set();
function computeDisplayStatus(op) {
    return computeDisplayStatusCore(op, executionStates, lastExecutionResults);
}
function computeVisibleOperations() {
    const result = computeFilterSetsCore({
        operations,
        executionStates,
        currentFilter,
        searchQuery,
        computeDisplayStatus
    });
    filteredOutNames = result.filteredOutNames;
    searchFilteredOutNames = result.searchFilteredOutNames;
    return result.visibleOperations;
}
const tableViewController = createTableViewController({
    tableHead: tableHead || undefined,
    tableBody: tableBody || undefined,
    tableStats: tableStats || undefined,
    getOperations: () => operations,
    getFilteredOperations: () => computeVisibleOperations(),
    getSelection: () => selection,
    setSelection: (nextSelection) => {
        selection = nextSelection;
    },
    onSelectionMutated: () => {
        updateSelectionUI();
        render();
    },
    computeDisplayStatus,
    enabledGlyph,
    buildRunPolicyText,
    buildTooltip,
    statusEmoji,
    overallStatusText
});
const phaseLegendController = createPhaseLegendController({
    phaseGroupsEl: phaseGroupsEl || undefined,
    legendEl: legendEl || undefined,
    getOperations: () => operations,
    getGraphVisibleNames: () => graphState.nodePositions.size ? new Set(graphState.nodePositions.keys()) : undefined,
    computeDisplayStatus,
    statusEmoji,
    overallStatusText,
    getStatusColors
});
function selectionChanged() {
    updateSelectionUI();
    render();
}
const graphSelectionController = createGraphSelectionController({
    graphEl,
    getCurrentView: () => currentView,
    getSelection: () => selection,
    setSelection: (nextSelection) => {
        selection = nextSelection;
    },
    getOperations: () => operations,
    getGraphNodePositions: () => graphState.nodePositions,
    graphNodeWidth: 28,
    graphNodeHeight: 28,
    onSelectionChanged: selectionChanged,
    onLiveSelectionChanged: updateGraph
});
function singleSelect(name) {
    graphSelectionController.singleSelect(name);
}
function toggleSelect(name) {
    graphSelectionController.toggleSelect(name);
}
const graphViewController = createGraphViewController({
    graphEl,
    edgesSvg,
    getOperations: () => operations,
    getExecutionStates: () => executionStates,
    getQueuedStates: () => queuedStates,
    getSelection: () => selection,
    getFilteredOutNames: () => filteredOutNames,
    getSearchFilteredOutNames: () => searchFilteredOutNames,
    getLastExecutionResults: () => lastExecutionResults,
    getComputeDisplayStatus: computeDisplayStatus,
    getStatusEmoji: statusEmoji,
    getOverallStatusText: overallStatusText,
    renderPhaseLegend: () => phaseLegendController.renderAll(),
    singleSelect,
    toggleSelect
});
graphSelectionController.wireGraphMarqueeSelection();
function markGraphDirty() {
    graphViewController.markGraphDirty();
    if (currentView === 'graph') {
        ensureGraph();
    }
}
function ensureGraph() {
    graphViewController.ensureGraph();
}
function updateGraph() {
    graphViewController.updateGraph();
}
function renderTable() {
    tableViewController.renderTable();
}
function render() {
    if (currentView === 'table') {
        renderTable();
    }
    else {
        ensureGraph();
    }
    updateSelectionUI();
}
function setConnected(connected) {
    setTopBarConnected(topBarRefs, connected, updateSelectionUI, disabledControlIds);
}
function updateDerivedUrlDisplay() {
    updateTopBarDerivedUrlDisplay(connectBtn);
}
function updateManagerState() {
    if (!graphSettings)
        return;
    updateTopBarManagerState(topBarRefs, graphSettings);
}
function log(message) {
    const time = new Date().toLocaleTimeString();
    window.console.log('[' + time + '] ' + message);
}
const socketController = createDashboardWebSocketController({
    getUrl: () => computeWebSocketUrl(window.location),
    onConnecting: () => {
        showConnectingStatus(statusPill, statusEmojiEl, statusEmoji);
    },
    onConnectedStateChange: (connected) => {
        setConnected(connected);
    },
    onOpen: () => {
        updateStatusPill();
    },
    onClose: () => {
        updateStatusPill();
    },
    onError: (event) => {
        log('WebSocket error: ' + event.type);
    },
    onParsedMessage: (message) => {
        handleMessage(message);
    },
    onParseError: (error) => {
        log('Bad JSON: ' + String(error));
    },
    onLog: log
});
function updateStatusPill() {
    updateTopBarStatusPill(topBarRefs, socketController.getSocket(), graphSettings, statusEmoji);
}
const selectionBarController = createSelectionBarController({
    getSelection: () => selection,
    getCurrentView: () => currentView,
    isConnected: () => socketController.isConnected()
});
function connect() {
    socketController.connect();
}
function disconnect() {
    socketController.disconnect();
}
function sendCommand(cmd) {
    socketController.sendCommand(cmd);
}
function handleMessage(msg) {
    switch (msg.event) {
        case 'sync': {
            setOperationsFromPayload(operations, msg.operations || []);
            executionStates.clear();
            applyExecutionStatesMutation(operations, executionStates, msg.currentExecutionStates || []);
            setQueuedStates(queuedStates, msg.queuedStates || []);
            graphSettings = msg.graphState;
            lastExecutionResults = toLastExecutionResultsMap(msg.resultByOperation || []);
            if (appTitleEl && msg.sessionInfo) {
                const title = `${msg.sessionInfo.actionName} — ${msg.sessionInfo.repositoryIdentifier}`;
                appTitleEl.textContent = title;
                document.title = title;
            }
            markGraphDirty();
            break;
        }
        case 'sync-operations': {
            patchOperationsFromPayload(operations, msg.operations || []);
            markGraphDirty();
            break;
        }
        case 'sync-graph-state': {
            graphSettings = msg.graphState;
            break;
        }
        case 'iteration-scheduled': {
            setQueuedStates(queuedStates, msg.queuedStates || []);
            break;
        }
        case 'before-execute':
        case 'status-change': {
            applyExecutionStatesMutation(operations, executionStates, msg.executionStates || []);
            break;
        }
        case 'after-execute': {
            applyExecutionStatesMutation(operations, executionStates, msg.executionStates || []);
            lastExecutionResults = toLastExecutionResultsMap(msg.resultByOperation || []);
            if (graphSettings && msg.status) {
                graphSettings.status = msg.status;
            }
            break;
        }
        case 'terminal-chunk': {
            terminalPane.appendChunk(msg.kind, msg.text);
            break;
        }
    }
    updateManagerState();
    updateStatusPill();
    render();
}
function updateSelectionUI() {
    selectionBarController.updateSelectionUI();
}
function expandSelectionDependencies() {
    graphSelectionController.expandSelectionDependencies();
}
function expandSelectionConsumers() {
    graphSelectionController.expandSelectionConsumers();
}
function wireActions() {
    wireMainBarActions({
        connect,
        disconnect,
        isConnected: () => socketController.isConnected(),
        sendCommand,
        getGraphSettings: () => graphSettings,
        debugBtn,
        verboseBtn,
        parallelismInput,
        playPauseBtn,
        getOperationNames: () => Array.from(operations.keys()),
        setSelection: (nextSelection) => {
            selection = nextSelection;
        },
        clearSelection: () => {
            selection.clear();
        },
        hasSelection: () => selection.size > 0,
        render
    });
    wireLeftBarActions({
        sendCommand,
        getSelection: () => selection,
        clearSelectionAndRender: () => {
            if (!selection.size)
                return;
            selection.clear();
            render();
        },
        expandSelectionDependencies,
        expandSelectionConsumers
    });
    wireViewBar({
        getView: () => currentView,
        setView: (nextView) => {
            currentView = nextView;
        },
        getFilter: () => currentFilter,
        setFilter: (nextFilter) => {
            currentFilter = nextFilter;
        },
        setSearchQuery: (nextSearchQuery) => {
            searchQuery = nextSearchQuery;
        },
        markGraphDirty,
        render
    });
}
function init() {
    const urlState = loadDashboardUrlState(window.location.search);
    currentView = urlState.view;
    currentFilter = urlState.filter;
    wireActions();
    updateDerivedUrlDisplay();
    updateSelectionUI();
    updateManagerState();
    updateStatusPill();
    connect();
    window.__rushServeDemo = {
        operations,
        selection
    };
}
init();
//# sourceMappingURL=dashboard.js.map